import clsx from "clsx";
import dayjs from "dayjs";
import { FC, useMemo, useState } from "react";
import { OnArgs } from "react-calendar";
import { useTranslation } from "react-i18next";
import { useGet3rdProductAvailability } from "../../api/queries/productQueries";
import {
  useBookingDispatch,
  useBookingState,
} from "../../hooks/useBookingState";
import { PA_NAME } from "../../types/product-attribute/product-attribute";
import { ProdAttrUtils } from "../../utils/productAttributeUtils";
import CalendarCustom from "../calendar/CalendarCustom";
import Spin from "../spin/Spin";
import { TimeSlotSelection } from "../timeSlotSelection/TimeSlotSelection";

export const ProdAttrCalendar: FC<
  {
    booka?: BookingAttributeDTO;
    pickTimeSlot: boolean;
  } & ProdAttrPickerModeProps
> = ({ booka, pickTimeSlot, ...props }) => {
  const { t } = useTranslation();
  const bookingDispatch = useBookingDispatch(props);
  const destDate = booka?.value?.destDate;
  const destTimeslot = booka?.value?.destTimeslot;
  const bookingId = props.bookingId || "";
  const bookingState = useBookingState(props);
  const bookingAttributes = bookingState.bookingAttributes;
  const defaultBooka = ProdAttrUtils.findDefaultBooka(bookingAttributes);
  const isLinked3rdParty = props.d?.isLinked3rdParty;

  const isMusical = defaultBooka?.productAttribute?.name === "MUSICAL";
  const isSim = defaultBooka?.productAttribute?.name === "SIM";

  // const { paId, broadwayAvailability } = useMusicalData({
  //   enabled: isMusical,
  //   bookingAttributes,
  // });

  const countPerson = ProdAttrUtils.countPerson(bookingState);

  const { data, isLoading } = useGet3rdProductAvailability({
    productId: bookingState?.product?.id || 0,
    // quantity: countPerson || 1,
    quantity: 1,
    month: bookingState.month, //dayjs(destDate).format("YYYY-MM")
    bookingId: bookingId,
    isLinked3rdParty: isMusical || !!isLinked3rdParty,
  });

  function normalizeTime(value: string): string {
    const [h, m] = value.split(":");
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  }

  const calendarConfig: TCalendarConfig[] | undefined = useMemo(() => {
    return data?.map((item) => ({
      ...item,
      availableGuest: Object.entries(item.availableGuest).reduce(
        (prev, cur) => {
          const time = normalizeTime(cur[0]);
          return {
            ...prev,
            [time]: cur[1],
          };
        },
        {},
      ),
      timeSlots: item.timeSlots.map((item) => normalizeTime(item)),
    }));
  }, [data]);

  const thirdPartyBals = useMemo(
    () =>
      isMusical
        ? bookingAttributes.filter((x) =>
            PA_NAME.PARTY_3RD.includes(
              x.productAttribute.name as ProdAttrName3rdParty,
            ),
          )
        : [],
    [bookingAttributes, isMusical],
  );

  const handleActiveStartDateChange = ({ activeStartDate }: OnArgs) => {
    const month = dayjs(activeStartDate).format("YYYY-MM");
    bookingDispatch.selectMonth({ month });
    booka &&
      bookingDispatch.bookingPickLs([
        {
          ...booka,
          value: {
            ...booka.value,
            pickLater:
              !booka.value?.pickLater ||
              ((!pickTimeSlot || booka?.value?.destTimeslot) && destDate)
                ? undefined
                : booka.value?.pickLater,
            destDate: undefined,
            destTimeslot: "",
          },
        },
        ...(isMusical
          ? (thirdPartyBals ?? []).map((x) => {
              return {
                ...x,
                value: undefined,
              };
            })
          : []),
      ]);
  };

  return (
    <div
      className={clsx(
        "mb-5 flex flex-col gap-6 sm:flex-row",
        props.mode === "POPUP" && "sm:flex-wrap",
      )}
    >
      <div className="min-w-full sm:min-w-[19rem]">
        <Spin spinning={isLoading}>
          <CalendarCustom
            enableDay={isSim}
            handleActiveStartDateChange={handleActiveStartDateChange}
            calendarConfig={calendarConfig}
            disablePastDates={true}
            defaultDate={destDate ? dayjs(destDate).toDate() : undefined}
            onDayClick={(date: Date) => {
              const destDate = dayjs(date).format("YYYYMMDD");
              booka &&
                bookingDispatch.bookingPickLs([
                  {
                    ...booka,
                    value: {
                      ...booka.value,
                      pickLater:
                        !booka.value?.pickLater ||
                        ((!pickTimeSlot || booka?.value?.destTimeslot) &&
                          destDate)
                          ? undefined
                          : booka.value?.pickLater,
                      destDate,
                      destTimeslot: "",
                    },
                  },
                  ...(isMusical
                    ? (thirdPartyBals ?? []).map((x) => {
                        return {
                          ...x,
                          value: undefined,
                        };
                      })
                    : []),
                ]);
            }}
          />
        </Spin>
      </div>

      {pickTimeSlot && (
        <TimeSlotSelection
          destTimeslot={destTimeslot}
          loading={isLoading}
          countPerson={countPerson}
          mode={props.mode}
          destDate={destDate}
          productId={props?.d?.id}
          calendarConfig={calendarConfig}
          activeTimeSlots={[destTimeslot].filter((x) => x)}
          onTimeSlotChange={(timeSlotValue: string) =>
            booka &&
            bookingDispatch.bookingPick({
              ...booka,
              value: {
                ...booka?.value,
                pickLater:
                  !booka.value?.pickLater ||
                  (timeSlotValue && booka?.value?.destDate)
                    ? undefined
                    : booka.value?.pickLater,
                destTimeslot: timeSlotValue, // eg: 0800, 1900
              },
            })
          }
        />
      )}
    </div>
  );
};
